import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Trophy, ChevronDown, ChevronRight, Settings2, EyeOff, Eye, GitMerge } from "lucide-react";

const DIV_COLOR = {
    TOP: "rgb(var(--tc-ffd700))",
    DOP: "rgb(var(--tc-2bb6ff))",
    OPS: "rgb(var(--tc-4caf50))",
    OP: "rgb(var(--tc-ff9800))",
    HOPE: "rgb(var(--tc-ff9800))",
    PRIMAVERA: "rgb(var(--tc-9c27b0))",
};

export default function Ranking() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [data, setData] = useState(null);
    const [mode, setMode] = useState("flat");
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState({});
    const [manage, setManage] = useState(false);
    const [busy, setBusy] = useState(false);

    const reload = useCallback(() => {
        setLoading(true);
        return api.get(`/ranking`, { params: { mode, ...(manage ? { manage: true } : {}) } })
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [mode, manage]);

    useEffect(() => { reload(); }, [reload]);

    const allRows = data?.ranking || [];
    const rows = allRows.filter((r) => r.has_team && !r.hidden);
    const teamlessRows = allRows.filter((r) => !r.has_team && !r.hidden);
    const hiddenRows = allRows.filter((r) => r.hidden);
    const mergeTargets = data?.merge_targets || [];
    const maxTotal = useMemo(() => Math.max(1, ...rows.map((r) => r.total)), [rows]);

    const doMerge = async (row, targetTeamId) => {
        if (!targetTeamId) return;
        const tgt = mergeTargets.find((m) => m.team_id === targetTeamId);
        if (!confirm(`Unire "${row.president}" (${row.titles_count} titoli) in "${tgt?.president}"?\nI titoli storici verranno accreditati a ${tgt?.president}.`)) return;
        setBusy(true);
        try {
            await api.post(`/ranking/merge`, { source_names: row.raw_winners || [row.president], target_team_id: targetTeamId });
            await reload();
        } catch (e) {
            alert(e.response?.data?.detail || "Errore durante l'unione");
        } finally { setBusy(false); }
    };

    const doHide = async (row) => {
        if (!confirm(`Nascondere "${row.president}" dal ranking?`)) return;
        setBusy(true);
        try { await api.post(`/ranking/hide`, { president: row.president }); await reload(); }
        catch (e) { alert(e.response?.data?.detail || "Errore"); }
        finally { setBusy(false); }
    };

    const doUnhide = async (row) => {
        setBusy(true);
        try { await api.post(`/ranking/unhide`, { president: row.president }); await reload(); }
        catch (e) { alert(e.response?.data?.detail || "Errore"); }
        finally { setBusy(false); }
    };

    return (
        <div className="max-w-5xl mx-auto px-4 py-8" data-testid="ranking-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
                <div>
                    <div className="flex items-center gap-3">
                        <Trophy className="w-8 h-8" style={{ color: "rgb(var(--tc-ffd700))" }} />
                        <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-tffffff">RANKING</h1>
                    </div>
                    <p className="op-mono text-xs text-t8f95a3 mt-2 max-w-xl leading-relaxed">
                        Classifica per presidente: palmarès delle ultime stagioni
                        {data?.seasons ? ` (${data.seasons[0]}–${data.seasons[data.seasons.length - 1]})` : ""} +
                        punti per la lega in cui milita ora.
                    </p>
                </div>
                <div className="flex items-center gap-2 self-start">
                    {isAdmin && (
                        <button
                            onClick={() => setManage((v) => !v)}
                            className={`op-mono text-[11px] uppercase px-3 py-2 rounded border transition-colors flex items-center gap-1.5 ${manage ? "border-t2bb6ff text-t2bb6ff bg-t2bb6ff/10" : "border-t22252a text-t8f95a3 hover:bg-t15181d"}`}
                            data-testid="ranking-manage-toggle"
                        >
                            <Settings2 className="w-3.5 h-3.5" /> Gestione
                        </button>
                    )}
                    <div className="flex border border-t22252a rounded overflow-hidden" data-testid="ranking-mode-toggle">
                        <button
                            onClick={() => setMode("flat")}
                            className={`op-mono text-[11px] uppercase px-3 py-2 transition-colors ${mode === "flat" ? "bg-tffd700 text-t0a0c0f" : "text-t8f95a3 hover:bg-t15181d"}`}
                            data-testid="ranking-mode-flat"
                        >
                            Attuale
                        </button>
                        <button
                            onClick={() => setMode("final")}
                            className={`op-mono text-[11px] uppercase px-3 py-2 transition-colors ${mode === "final" ? "bg-tffd700 text-t0a0c0f" : "text-t8f95a3 hover:bg-t15181d"}`}
                            data-testid="ranking-mode-final"
                            title="Proiezione con i punti a scalare in base alla classifica attuale delle 4 leghe"
                        >
                            Proiezione fine stagione
                        </button>
                    </div>
                </div>
            </div>

            {isAdmin && manage && (
                <ManagePanel
                    teamlessRows={teamlessRows}
                    hiddenRows={hiddenRows}
                    mergeTargets={mergeTargets}
                    busy={busy}
                    onMerge={doMerge}
                    onHide={doHide}
                    onUnhide={doUnhide}
                />
            )}

            {loading && <div className="op-mono text-sm text-t8f95a3">Caricamento…</div>}

            {!loading && rows.length > 0 && (
                <div className="border border-t22252a rounded-lg overflow-hidden">
                    <div className="hidden sm:grid grid-cols-[48px_1fr_90px_90px_120px] gap-2 px-4 py-2.5 bg-t15181d op-mono text-[10px] uppercase tracking-wider text-t5c6370">
                        <div>#</div><div>Presidente</div>
                        <div className="text-right">Palmarès</div>
                        <div className="text-right">Posizione</div>
                        <div className="text-right">Totale</div>
                    </div>
                    {rows.map((r) => {
                        const isOpen = !!open[r.president];
                        const rankColor = r.rank === 1 ? "rgb(var(--tc-ffd700))" : r.rank === 2 ? "#c0c7d0" : r.rank === 3 ? "#cd7f32" : "rgb(var(--tc-5c6370))";
                        return (
                            <div key={r.president} className="border-t border-t22252a">
                                <button
                                    onClick={() => setOpen((o) => ({ ...o, [r.president]: !o[r.president] }))}
                                    className="w-full grid grid-cols-[48px_1fr_90px] sm:grid-cols-[48px_1fr_90px_90px_120px] gap-2 px-4 py-3 items-center text-left hover:bg-t15181d/60 transition-colors"
                                    data-testid={`ranking-row-${r.rank}`}
                                >
                                    <div className="op-mono font-black text-lg" style={{ color: rankColor }}>{r.rank}</div>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-1.5">
                                            {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-t5c6370" /> : <ChevronRight className="w-3.5 h-3.5 text-t5c6370" />}
                                            <span className="font-bold text-tffffff truncate">{r.president}</span>
                                        </div>
                                        <div className="flex flex-wrap gap-1 mt-1 pl-5">
                                            {r.current.filter((c) => c.division !== "PRIMAVERA").map((c, i) => (
                                                <span key={i} className="op-mono text-[9px] px-1.5 py-0.5 rounded border"
                                                    style={{ color: DIV_COLOR[c.division] || "#888", borderColor: (DIV_COLOR[c.division] || "#888") + "55" }}>
                                                    {c.team} · {c.division === "HOPE" ? "OP" : c.division}{c.rank ? ` (${c.rank}°)` : ""}
                                                </span>
                                            ))}
                                            {r.current.filter((c) => c.division !== "PRIMAVERA").length === 0 && (
                                                <span className="op-mono text-[9px] text-t5c6370">nessuna squadra attuale</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-right op-mono text-sm text-tffd700">{r.palmares}</div>
                                    <div className="hidden sm:block text-right op-mono text-sm text-t2bb6ff">{r.position}</div>
                                    <div className="hidden sm:block text-right">
                                        <span className="op-mono font-black text-lg text-tffffff">{r.total}</span>
                                        <div className="h-1 mt-1 rounded bg-t22252a overflow-hidden">
                                            <div className="h-full" style={{ width: `${(r.total / maxTotal) * 100}%`, background: "rgb(var(--tc-ffd700))" }} />
                                        </div>
                                    </div>
                                </button>
                                {isOpen && (
                                    <div className="px-4 pb-4 pt-1 bg-t0a0c0f/60" data-testid={`ranking-detail-${r.rank}`}>
                                        <div className="op-mono text-[10px] uppercase tracking-wider text-t5c6370 mb-2">
                                            Titoli ({r.titles_count})
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {r.titles.map((t, i) => (
                                                <span key={i} className="op-mono text-[10px] px-2 py-1 rounded border border-t22252a text-tc9d2dc">
                                                    <span className="text-tffd700">S{t.season}</span> {t.label}
                                                    <span className="text-t5c6370"> +{t.points}</span>
                                                </span>
                                            ))}
                                            {r.titles.length === 0 && <span className="op-mono text-[10px] text-t5c6370">nessun titolo nelle stagioni considerate</span>}
                                        </div>
                                        {mode === "final" && (r.cup_runs?.length || 0) > 0 && (
                                            <>
                                                <div className="op-mono text-[10px] uppercase tracking-wider text-t5c6370 mt-4 mb-2">
                                                    Cammino in coppa (proiezione)
                                                </div>
                                                <div className="flex flex-wrap gap-1.5" data-testid={`ranking-cupruns-${r.rank}`}>
                                                    {r.cup_runs.map((c, i) => (
                                                        <span key={i} className="op-mono text-[10px] px-2 py-1 rounded border border-t22252a text-tc9d2dc">
                                                            {c.cup} · <span className="text-t2bb6ff">{c.stage}</span>
                                                            <span className="text-t5c6370"> +{c.points}</span>
                                                        </span>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {!loading && rows.length === 0 && (
                <div className="op-mono text-sm text-t8f95a3">Nessun dato disponibile.</div>
            )}
        </div>
    );
}

function ManagePanel({ teamlessRows, hiddenRows, mergeTargets, busy, onMerge, onHide, onUnhide }) {
    return (
        <div className="border border-t2bb6ff/40 rounded-lg bg-t2bb6ff/[0.04] p-4 mb-6" data-testid="ranking-manage-panel">
            <div className="op-mono text-[11px] uppercase tracking-wider text-t2bb6ff mb-1 flex items-center gap-1.5">
                <Settings2 className="w-3.5 h-3.5" /> Gestione presidenti (solo admin)
            </div>
            <p className="op-mono text-[10px] text-t8f95a3 mb-4 leading-relaxed">
                Il ranking pubblico mostra solo i presidenti con una squadra attuale. Qui puoi <b>unire</b> i
                nomi storici dell'Albo (senza squadra) a un presidente attuale, o <b>nascondere</b> un presidente.
            </p>

            <div className="op-mono text-[10px] uppercase tracking-wider text-t5c6370 mb-2">
                Nomi storici senza squadra ({teamlessRows.length})
            </div>
            {teamlessRows.length === 0 && (
                <div className="op-mono text-[11px] text-t5c6370 mb-4">Nessuno — tutti i nomi dell'Albo sono associati a un presidente attuale.</div>
            )}
            <div className="space-y-2 mb-5">
                {teamlessRows.map((r) => (
                    <MergeRow key={r.president} row={r} mergeTargets={mergeTargets} busy={busy} onMerge={onMerge} onHide={onHide} />
                ))}
            </div>

            {hiddenRows.length > 0 && (
                <>
                    <div className="op-mono text-[10px] uppercase tracking-wider text-t5c6370 mb-2">
                        Presidenti nascosti ({hiddenRows.length})
                    </div>
                    <div className="space-y-2">
                        {hiddenRows.map((r) => (
                            <div key={r.president} className="flex items-center justify-between gap-3 border border-t22252a rounded px-3 py-2">
                                <span className="text-sm text-t8f95a3 truncate">{r.president}</span>
                                <button
                                    onClick={() => onUnhide(r)}
                                    disabled={busy}
                                    className="op-mono text-[10px] uppercase px-2 py-1 rounded border border-t7ce38b/50 text-t7ce38b hover:bg-t7ce38b/10 flex items-center gap-1"
                                    data-testid={`ranking-unhide-${r.president}`}
                                >
                                    <Eye className="w-3 h-3" /> Mostra
                                </button>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

function MergeRow({ row, mergeTargets, busy, onMerge, onHide }) {
    const [target, setTarget] = useState("");
    return (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 border border-t22252a rounded px-3 py-2" data-testid={`ranking-teamless-${row.president}`}>
            <div className="flex-1 min-w-0">
                <span className="text-sm text-tffffff truncate">{row.president}</span>
                <span className="op-mono text-[10px] text-t5c6370 ml-2">{row.titles_count} titoli · {row.palmares} pt</span>
            </div>
            <div className="flex items-center gap-2">
                <select
                    value={target}
                    onChange={(e) => setTarget(e.target.value)}
                    className="op-mono text-[11px] bg-t0e1014 border border-t22252a rounded px-2 py-1.5 text-tc9ccd3 max-w-[180px]"
                    data-testid={`ranking-merge-select-${row.president}`}
                >
                    <option value="">Unisci a…</option>
                    {mergeTargets.map((m) => (
                        <option key={m.team_id} value={m.team_id}>{m.president} ({m.team_name})</option>
                    ))}
                </select>
                <button
                    onClick={() => onMerge(row, target)}
                    disabled={busy || !target}
                    className="op-mono text-[10px] uppercase px-2 py-1.5 rounded border border-t2bb6ff/50 text-t2bb6ff hover:bg-t2bb6ff/10 disabled:opacity-40 flex items-center gap-1"
                    data-testid={`ranking-merge-btn-${row.president}`}
                >
                    <GitMerge className="w-3 h-3" /> Unisci
                </button>
                <button
                    onClick={() => onHide(row)}
                    disabled={busy}
                    className="op-mono text-[10px] uppercase px-2 py-1.5 rounded border border-tff4d4d/40 text-tff4d4d hover:bg-tff4d4d/10 flex items-center gap-1"
                    data-testid={`ranking-hide-${row.president}`}
                >
                    <EyeOff className="w-3 h-3" /> Nascondi
                </button>
            </div>
        </div>
    );
}

